import React, { useState, useMemo } from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  Clock,
  ExternalLink,
  Filter,
  Calendar,
  DollarSign,
  TrendingUp
} from 'lucide-react';
import { AdminSession } from '../types/admin.types';
import { getReportUrl } from '../utils/calculatorPathUtils';

interface SessionListProps {
  sessions: AdminSession[];
  loading?: boolean;
  title?: string;
  onSelectSession?: (session: AdminSession) => void;
}

const SessionList: React.FC<SessionListProps> = ({ 
  sessions, 
  loading = false,
  title = 'Sessions',
  onSelectSession
}) => {
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredSessions = useMemo(() => {
    if (statusFilter === 'all') return sessions;
    return sessions.filter(s => s.status === statusFilter);
  }, [sessions, statusFilter]);

  const statusCounts = useMemo(() => {
    const counts = {
      all: sessions.length,
      finalized: 0,
      complete: 0,
      in_progress: 0
    };
    
    sessions.forEach(s => {
      if (s.status === 'finalized') counts.finalized++;
      else if (s.status === 'complete') counts.complete++;
      else if (s.status === 'in_progress') counts.in_progress++;
    });
    
    return counts;
  }, [sessions]);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'complete':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'in_progress':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: AdminSession['status']) => {
    switch(status) {
      case 'finalized':
        return 'Finalized';
      case 'complete':
        return 'Complete';
      case 'in_progress':
        return 'In Progress';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with Filters */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
            {title}
          </h3>
        </div>
        
        {/* Status Filter Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { value: 'all', label: 'All', count: statusCounts.all },
            { value: 'finalized', label: 'Finalized', count: statusCounts.finalized, color: 'text-green-600' },
            { value: 'complete', label: 'Complete', count: statusCounts.complete, color: 'text-blue-600' },
            { value: 'in_progress', label: 'In Progress', count: statusCounts.in_progress, color: 'text-yellow-600' }
          ].map(filter => (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`
                px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-medium rounded-lg transition-colors whitespace-nowrap
                ${statusFilter === filter.value
                  ? 'bg-white shadow-sm border border-gray-300'
                  : 'hover:bg-gray-100'
                }
              `}
            >
              <span className={filter.color || 'text-gray-700'}>
                {filter.label}
              </span>
              <span className="ml-1 text-gray-500">
                ({filter.count})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {filteredSessions.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">
              No sessions found {statusFilter !== 'all' && `with status "${getStatusLabel(statusFilter as any)}"`}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredSessions.map((session) => (
              <div
                key={session.sessionId}
                className="px-4 sm:px-6 py-3 sm:py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onSelectSession?.(session)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="mt-1">{getStatusIcon(session.status)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                        <span className={`
                          text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium
                          ${session.status === 'finalized' ? 'bg-green-100 text-green-700' :
                            session.status === 'complete' ? 'bg-blue-100 text-blue-700' :
                            session.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-500'}
                        `}>
                          {getStatusLabel(session.status)}
                        </span>
                        {session.isRoosterAssessment && (
                          <span className="bg-purple-100 text-purple-700 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                            <span className="hidden sm:inline">Rooster Assessment</span>
                            <span className="sm:hidden">Rooster</span>
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-2">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                          <span className="truncate">{formatDate(session.finalizedAt || session.completedAt || session.startedAt)}</span>
                        </div>
                        {session.currentTAS && (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            TAS: {Math.round(session.currentTAS)}
                            {session.projectedTAS && (
                              <span className="text-gray-400">
                                → {Math.round(session.projectedTAS)}
                              </span>
                            )}
                          </div>
                        )}
                        {session.totalAnnualSavings && (
                          <div className="flex items-center gap-1 text-green-600">
                            <DollarSign className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            <span className="hidden sm:inline">{Math.round(session.totalAnnualSavings).toLocaleString()}</span>
                            <span className="sm:hidden">${Math.round(session.totalAnnualSavings / 1000)}K</span>
                          </div>
                        )}
                      </div>

                      {/* Session Details */}
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        <div className="flex flex-wrap gap-1 sm:gap-3">
                          {/* Show lead source for ALL sessions */}
                          {session.leadSource && (
                            <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
                              Source: {session.leadSource}
                            </span>
                          )}
                          
                          {/* Show additional details for finalized/complete sessions */}
                          {(session.status === 'finalized' || session.status === 'complete') && (
                            <>
                              {session.formData?.industry && (
                                <span>Industry: {session.formData.industry}</span>
                              )}
                              {session.formData?.companySize && (
                                <span>Size: {session.formData.companySize}</span>
                              )}
                              {session.emailsSent > 0 && (
                                <span>{session.emailsSent} email{session.emailsSent !== 1 ? 's' : ''} sent</span>
                              )}
                            </>
                          )}
                          
                          {/* Show step info for in-progress sessions */}
                          {session.status === 'in_progress' && session.currentStep && (
                            <span>{session.isEditSession ? 'Editing' : `Step ${session.currentStep} of 4`}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  {session.status === 'finalized' && session.reportId && (
                    <a
                      href={getReportUrl(session)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg transition-colors self-start sm:ml-4"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">View Report</span>
                      <span className="sm:hidden">Report</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionList;